import React, { useState } from 'react';

const EXPLODE_PX = 10;

function buildPiePaths(labels, values, colors, cx, cy, r) {
  const total = values.reduce((s, v) => s + v, 0);
  if (!total) return [];

  const slices = labels
    .map((label, i) => ({ label, value: values[i], color: colors[i] }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);

  if (slices.length === 1) {
    return [{ circle: true, color: slices[0].color, label: slices[0].label, value: slices[0].value, pct: 100, midAngle: 0 }];
  }

  const toXY = (angle) => [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];

  let startAngle = -Math.PI / 2;
  return slices.map(({ label, value, color }) => {
    const pct = value / total;
    const endAngle = startAngle + pct * 2 * Math.PI;
    const midAngle = (startAngle + endAngle) / 2;
    const [x1, y1] = toXY(startAngle);
    const [x2, y2] = toXY(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    startAngle = endAngle;
    return { d, color, label, value, pct: Math.round(pct * 100), midAngle };
  });
}

function SvgPieChart({ title, labels, values, colors, size = 220 }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  const slices = buildPiePaths(labels, values, colors, cx, cy, r);
  const total = values.reduce((s, v) => s + v, 0);

  const legendItems = labels
    .map((label, i) => ({ label, value: values[i], color: colors[i] }))
    .sort((a, b) => b.value - a.value);

  if (!slices.length) {
    return <div className="nd-no-data">No data available.</div>;
  }

  // Render hovered slice last so it paints on top
  const renderOrder = slices
    .map((s, i) => ({ ...s, originalIndex: i }))
    .sort((a, b) => {
      if (a.originalIndex === hoveredIndex) return 1;
      if (b.originalIndex === hoveredIndex) return -1;
      return 0;
    });

  return (
    <div className="svg-pie-card">
      <h4 className="chart-title">{title}</h4>
      <div className="nd-svg-pie-wrap">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="nd-svg-pie"
          style={{ overflow: 'visible' }}
        >
          {renderOrder.map((s) => {
            const i = s.originalIndex;
            const isHovered = hoveredIndex === i;

            if (s.circle) {
              return (
                <circle
                  key={i}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={s.color}
                  style={{
                    transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                    transformOrigin: `${cx}px ${cy}px`,
                    // always-present shadow — interpolates cleanly, no white flash
                    filter: isHovered
                      ? 'drop-shadow(0 6px 8px rgba(0,0,0,0.38))'
                      : 'drop-shadow(0 0px 0px rgba(0,0,0,0))',
                    transition: 'transform 0.2s ease, filter 0.2s ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <title>{s.label}: {s.value} (100%)</title>
                </circle>
              );
            }

            const dx = isHovered ? Math.cos(s.midAngle) * EXPLODE_PX : 0;
            const dy = isHovered ? Math.sin(s.midAngle) * EXPLODE_PX : 0;
            return (
              <g
                key={i}
                transform={`translate(${dx}, ${dy})`}
                style={{
                  // always-present shadow — browser interpolates between the two states,
                  // no white flash because the filter is never toggled on/off
                  filter: isHovered
                    ? 'drop-shadow(0 6px 8px rgba(0,0,0,0.38))'
                    : 'drop-shadow(0 0px 0px rgba(0,0,0,0))',
                  transition: 'filter 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <path d={s.d} fill={s.color}>
                  <title>{s.label}: {s.value} ({s.pct}%)</title>
                </path>
              </g>
            );
          })}
        </svg>
        <div className="nd-pie-legend">
          {legendItems.map((s) => {
            const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
            const sliceIndex = slices.findIndex((sl) => sl.label === s.label);
            const isHovered = hoveredIndex === sliceIndex;
            return (
              <div
                key={s.label}
                className="nd-pie-legend-item"
                style={{ fontWeight: isHovered ? 700 : undefined }}
                onMouseEnter={() => sliceIndex >= 0 && setHoveredIndex(sliceIndex)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <span className="nd-pie-legend-dot" style={{ background: s.color }} />
                <span className="nd-pie-legend-label">{s.label}</span>
                <span className="nd-pie-legend-pct">{s.value.toLocaleString()} &middot; {pct}%</span>
              </div>
            );
          })}
          <div className="nd-pie-legend-total">Total: {total.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}

export default SvgPieChart;
